import { Source } from '@highimpact-frontend/api-types';
import { timezoneFromSource } from './curveTimezoneMapping';
import {
  createInitialPlotPreferences,
  upgradePlotPreferences,
} from './plotPreferences';
import {
  resolveAxisKeyForSource,
  SCATTER_X_AXIS_KEY,
} from './scatterAxisKeys';
import { AxisKey, CurveId, CurveRelation, PlotPreferences } from './plotTypes';

type DataSourceInitSettings = NonNullable<
  Parameters<typeof createInitialPlotPreferences>[0]
>[number];
type AdditionalSetttings = Parameters<typeof createInitialPlotPreferences>[2];

function makeCurveId(source: Source, occurrence: number): string {
  const base = `mp:${source.measure_point_id}:mt:${source.measure_type_id}`;
  return occurrence === 0 ? base : `${base}:occ:${occurrence}`;
}

/**
 * Generates initial settings for each data source,
 * ensuring unique curve IDs -- assuming the sources are also unique -- and using a best-effort approach to create appropriate axis names.
 * For scatter plots, the last source is designated as the x-axis and given thicker markers for visibility.
 *
 * @param sources - The array of data sources to generate settings for.
 * @param areSourcesForScatterPlot - Whether the sources are for a scatter plot, which affects axis assignment and marker thickness.
 * @returns An array of DataSourceInitSettings for each source.
 */
function dataSourceInitSettingsFromSources(
  sources: Source[],
  areSourcesForScatterPlot = false
): DataSourceInitSettings[] {
  const baseIdOccurrences: Record<string, number> = {};
  const unitToUnitCount = sources
    .filter((_, i, arr) => !areSourcesForScatterPlot || i !== arr.length - 1)
    .reduce((acc, source) => {
      const unit = source.measurement_unit;
      acc[unit] = (acc[unit] ?? 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  return sources.map((source, i, arr) => {
    const baseId = `mp:${source.measure_point_id}:mt:${source.measure_type_id}`;
    const occurrence = baseIdOccurrences[baseId] ?? 0;
    baseIdOccurrences[baseId] = occurrence + 1;

    const isX = areSourcesForScatterPlot && i === arr.length - 1;

    return {
      curveId: makeCurveId(source, occurrence),
      sourceIndex: i,
      axisKey: resolveAxisKeyForSource(
        source.measurement_unit,
        areSourcesForScatterPlot,
        i,
        arr.length
      ),
      timezone: timezoneFromSource(source),
      curveName: source.label,
      axisName:
        unitToUnitCount[source.measurement_unit] > 1
          ? `${source.physical_quantity} (${source.measurement_unit})`
          : source.label,
      // Scatter plots need thick markers to be visible
      thicknessPx: areSourcesForScatterPlot ? 10 : 2,
      // For scatter plots, we use the last source as the x-axis
      isX,
    };
  });
}

export function plotPreferencesFromSources(
  sources: Source[],
  areSourcesForScatterPlot = false,
  additionalSettings?: AdditionalSetttings,
  previousPreferences?: PlotPreferences
): PlotPreferences {
  const resolvedPreviousPreferences = previousPreferences
    ? upgradePlotPreferences(previousPreferences)
    : undefined;
  const dataSourceInitSettings = dataSourceInitSettingsFromSources(
    sources,
    areSourcesForScatterPlot
  );

  const initialPreferences = createInitialPlotPreferences(
    dataSourceInitSettings,
    undefined,
    {
      showRangeSlider:
        additionalSettings?.showRangeSlider ??
        resolvedPreviousPreferences?.showRangeSlider,
      curveRelations:
        additionalSettings?.curveRelations ??
        resolvedPreviousPreferences?.curveRelations,
      thresholdPrefs:
        additionalSettings?.thresholdPrefs ??
        resolvedPreviousPreferences?.thresholdPrefs,
      toolBarPrefs:
        additionalSettings?.toolBarPrefs ??
        resolvedPreviousPreferences?.toolBarPrefs,
      timeseriesAxisLabelMode:
        additionalSettings?.timeseriesAxisLabelMode ??
        resolvedPreviousPreferences?.timeseriesAxisLabelMode,
    }
  );

  if (!resolvedPreviousPreferences) {
    return initialPreferences;
  }

  const existingCurveIds = new Set(
    Object.keys(initialPreferences.curvePrefs) as CurveId[]
  );
  const existingAxisKeys = new Set(
    Object.keys(initialPreferences.axisPrefs) as AxisKey[]
  );

  const mergedCurvePrefs: PlotPreferences['curvePrefs'] = {
    ...initialPreferences.curvePrefs,
  };
  (Object.keys(mergedCurvePrefs) as CurveId[]).forEach((curveId) => {
    if (resolvedPreviousPreferences.curvePrefs[curveId]) {
      mergedCurvePrefs[curveId] = {
        ...mergedCurvePrefs[curveId],
        ...resolvedPreviousPreferences.curvePrefs[curveId],
      };
    }
  });

  const mergedAxisPrefs: PlotPreferences['axisPrefs'] = {
    ...initialPreferences.axisPrefs,
  };
  (Object.keys(mergedAxisPrefs) as AxisKey[]).forEach((axisKey) => {
    if (resolvedPreviousPreferences.axisPrefs[axisKey]) {
      mergedAxisPrefs[axisKey] = {
        ...mergedAxisPrefs[axisKey],
        ...resolvedPreviousPreferences.axisPrefs[axisKey],
      };
    }
  });

  const mergedDataSourceToAxis: PlotPreferences['dataSourceToAxis'] = {
    ...initialPreferences.dataSourceToAxis,
  };
  (Object.keys(mergedDataSourceToAxis) as CurveId[]).forEach((curveId) => {
    const initialAxis = initialPreferences.dataSourceToAxis[curveId];
    const previousAxis = resolvedPreviousPreferences.dataSourceToAxis[curveId];
    if (previousAxis && existingAxisKeys.has(previousAxis)) {
      if (
        initialAxis === SCATTER_X_AXIS_KEY &&
        previousAxis !== SCATTER_X_AXIS_KEY
      ) {
        return;
      }
      mergedDataSourceToAxis[curveId] = previousAxis;
    }
  });

  const initialCurveOrder =
    initialPreferences.curveOrder ??
    (Object.keys(initialPreferences.curvePrefs) as CurveId[]);

  const mergedCurveOrder = [
    ...(resolvedPreviousPreferences.curveOrder ?? []).filter((curveId) =>
      existingCurveIds.has(curveId)
    ),
    ...initialCurveOrder.filter(
      (curveId) =>
        !(resolvedPreviousPreferences.curveOrder ?? []).includes(curveId)
    ),
  ];

  const initialAxisOrderSet = new Set(initialPreferences.yAxisOrder);
  const mergedYAxisOrder = [
    ...(resolvedPreviousPreferences.yAxisOrder ?? []).filter((axisKey) =>
      initialAxisOrderSet.has(axisKey)
    ),
    ...initialPreferences.yAxisOrder.filter(
      (axisKey) =>
        !(resolvedPreviousPreferences.yAxisOrder ?? []).includes(axisKey)
    ),
  ];

  const sanitizeRelation = (
    relation: CurveRelation
  ): CurveRelation | undefined => {
    if (!existingCurveIds.has(relation.mainCurve)) {
      return undefined;
    }

    const relatedCurves = relation.relatedCurves.filter((curveId) =>
      existingCurveIds.has(curveId)
    );

    if (relation.relationType === 'interval' && relatedCurves.length < 2) {
      return undefined;
    }
    if (relation.relationType !== 'interval' && relatedCurves.length < 1) {
      return undefined;
    }

    return {
      ...relation,
      relatedCurves,
    };
  };

  const mergedCurveRelations = (
    additionalSettings?.curveRelations ??
    resolvedPreviousPreferences.curveRelations ??
    []
  )
    .map(sanitizeRelation)
    .filter((relation): relation is CurveRelation => relation !== undefined);

  return {
    ...initialPreferences,
    curvePrefs: mergedCurvePrefs,
    curveOrder: mergedCurveOrder,
    // Always derive timezones from current sources so backend corrections propagate on re-selection.
    curveToTimezone: initialPreferences.curveToTimezone,
    axisPrefs: mergedAxisPrefs,
    dataSourceToAxis: mergedDataSourceToAxis,
    yAxisOrder: mergedYAxisOrder,
    curveRelations: mergedCurveRelations,
    showRangeSlider:
      additionalSettings?.showRangeSlider ??
      resolvedPreviousPreferences.showRangeSlider ??
      initialPreferences.showRangeSlider,
    thresholdPrefs:
      additionalSettings?.thresholdPrefs ??
      resolvedPreviousPreferences.thresholdPrefs ??
      initialPreferences.thresholdPrefs,
    toolBarPrefs: {
      ...initialPreferences.toolBarPrefs,
      ...(resolvedPreviousPreferences.toolBarPrefs ?? {}),
      ...(additionalSettings?.toolBarPrefs ?? {}),
    },
    timeseriesAxisLabelMode:
      additionalSettings?.timeseriesAxisLabelMode ??
      resolvedPreviousPreferences.timeseriesAxisLabelMode ??
      initialPreferences.timeseriesAxisLabelMode,
  };
}
